/**
 * API de facturación electrónica: series y comprobantes.
 */
import { apiFetch } from "./client";

export type TipoComprobante = "BOLETA" | "FACTURA";
export type EstadoComprobante = "PENDIENTE" | "ACEPTADO" | "RECHAZADO" | "ANULADO";
export type TipoDocCliente = "DNI" | "RUC" | "CE" | "PASAPORTE" | "SIN_DOC";
export type Moneda = "PEN" | "USD";
export type TipoAfectacionIGV = "GRAVADO" | "EXONERADO" | "INAFECTO";

// ---------- Series ----------

export interface Serie {
  id: number;
  sede_id: number;
  tipo_comprobante: TipoComprobante;
  serie: string;
  correlativo_actual: number;
  activa: boolean;
}

export interface SerieCreatePayload {
  sede_id: number;
  tipo_comprobante: TipoComprobante;
  serie: string;
  correlativo_inicial?: number;
}

export function listSeries(sede_id?: number): Promise<Serie[]> {
  const qs = sede_id ? `?sede_id=${sede_id}` : "";
  return apiFetch<Serie[]>(`/v1/series${qs}`);
}

export function createSerie(payload: SerieCreatePayload): Promise<Serie> {
  return apiFetch<Serie>("/v1/series", { method: "POST", body: payload });
}

export function toggleSerie(id: number, activa: boolean): Promise<Serie> {
  return apiFetch<Serie>(`/v1/series/${id}`, {
    method: "PATCH",
    body: { activa },
  });
}

// ---------- Comprobantes ----------

export interface ComprobanteItem {
  id: number;
  orden: number;
  descripcion: string;
  codigo_producto: string | null;
  unidad_medida: string;
  cantidad: string;
  precio_unitario: string;
  tipo_afectacion_igv: TipoAfectacionIGV;
  subtotal: string;
  igv: string;
  total: string;
}

export interface Comprobante {
  id: number;
  sede_id: number;
  serie_id: number;

  tipo_comprobante: TipoComprobante;
  serie: string;
  correlativo: number;
  numero_completo: string;
  fecha_emision: string;

  cliente_tipo_doc: TipoDocCliente;
  cliente_numero_doc: string | null;
  cliente_razon_social: string;
  cliente_direccion: string | null;
  cliente_email: string | null;

  paciente_id: number | null;
  cita_id: number | null;
  episodio_id: number | null;

  moneda: Moneda;
  subtotal_gravado: string;
  subtotal_exonerado: string;
  subtotal_inafecto: string;
  igv: string;
  total: string;

  estado: EstadoComprobante;
  motivo_anulacion: string | null;
  anulado_en: string | null;

  provider: string;
  provider_id: string | null;
  error_mensaje: string | null;

  xml_storage_key: string | null;
  pdf_storage_key: string | null;
  cdr_storage_key: string | null;

  emitido_por_user_id: number | null;
  created_at: string;

  items: ComprobanteItem[];
}

export interface ComprobanteItemInput {
  descripcion: string;
  codigo_producto?: string | null;
  unidad_medida?: string;
  cantidad: string;
  precio_unitario: string;
  tipo_afectacion_igv: TipoAfectacionIGV;
}

export interface ComprobanteCliente {
  tipo_doc: TipoDocCliente;
  numero_doc?: string | null;
  razon_social: string;
  direccion?: string | null;
  email?: string | null;
}

export interface ComprobanteCreatePayload {
  tipo_comprobante: TipoComprobante;
  serie_id: number;
  sede_id: number;
  cliente: ComprobanteCliente;
  moneda?: Moneda;
  items: ComprobanteItemInput[];
  paciente_id?: number | null;
  cita_id?: number | null;
  episodio_id?: number | null;
}

export interface ListComprobantesParams {
  paciente_id?: number;
  sede_id?: number;
  estado?: EstadoComprobante;
  tipo_comprobante?: TipoComprobante;
  limit?: number;
}

export function listComprobantes(params: ListComprobantesParams = {}): Promise<Comprobante[]> {
  const qs = new URLSearchParams();
  if (params.paciente_id) qs.set("paciente_id", String(params.paciente_id));
  if (params.sede_id) qs.set("sede_id", String(params.sede_id));
  if (params.estado) qs.set("estado", params.estado);
  if (params.tipo_comprobante) qs.set("tipo_comprobante", params.tipo_comprobante);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString();
  return apiFetch<Comprobante[]>(`/v1/comprobantes${q ? `?${q}` : ""}`);
}

export function getComprobante(id: number): Promise<Comprobante> {
  return apiFetch<Comprobante>(`/v1/comprobantes/${id}`);
}

export function emitirComprobante(payload: ComprobanteCreatePayload): Promise<Comprobante> {
  return apiFetch<Comprobante>("/v1/comprobantes", {
    method: "POST",
    body: payload,
  });
}

export function anularComprobante(id: number, motivo: string): Promise<Comprobante> {
  return apiFetch<Comprobante>(`/v1/comprobantes/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}

export function consultarEstadoComprobante(id: number): Promise<Comprobante> {
  return apiFetch<Comprobante>(`/v1/comprobantes/${id}/consultar`, {
    method: "POST",
  });
}
